import type { ComponentPropsWithoutRef } from 'react';

import InlineMessage from '@ingka/inline-message';
import informationCircle from '@ingka/ssr-icon/paths/information-circle';

type InlineMessageProps = ComponentPropsWithoutRef<typeof InlineMessage>;
type BannerVariant = Exclude<InlineMessageProps['variant'], undefined>;
type BannerIcon = InlineMessageProps['ssrIcon'];

export type MessageBannerState = 'idle' | 'add-success' | 'add-error' | 'edit-success' | 'edit-error';

interface MessageBannerDefinition {
    titleKey: string;
    bodyKey?: string;
    variant: BannerVariant;
    ssrIcon?: BannerIcon;
}

interface Props {
    state: MessageBannerState;
    translate: (key: string) => string;
    dismissable?: boolean;
}

const MESSAGE_DEFINITIONS: Record<Exclude<MessageBannerState, 'idle'>, MessageBannerDefinition> = {
    'add-success': {
        titleKey: 'messages_notifier_message_added_title',
        bodyKey: 'messages_notifier_message_added_body',
        variant: 'positive',
    },
    'add-error': {
        titleKey: 'messages_notifier_message_failed_to_add_title',
        variant: 'negative',
        ssrIcon: informationCircle,
    },
    'edit-success': {
        titleKey: 'messages_notifier_message_edited_title',
        variant: 'informative',
        ssrIcon: informationCircle,
    },
    'edit-error': {
        titleKey: 'messages_notifier_message_failed_to_edit_title',
        variant: 'negative',
        ssrIcon: informationCircle,
    },
};

export const MessageBanner = ({ state, translate, dismissable = true }: Props) => {
    if (state === 'idle') {
        return null;
    }

    const { titleKey, bodyKey, variant, ssrIcon } = MESSAGE_DEFINITIONS[state];

    const inlineMessageProps: InlineMessageProps = {
        title: translate(titleKey),
        variant,
        dismissable,
        ...(bodyKey !== undefined ? { body: translate(bodyKey) } : {}),
        ...(ssrIcon !== undefined ? { ssrIcon } : {}),
    };

    return <InlineMessage {...inlineMessageProps} />;
};
